import { useCallback, useState } from 'react';
import { Position, MenuIcon, SideSheet, Pane, ArrowRightIcon } from 'evergreen-ui';
import { useMediaQuery } from 'react-responsive';
import { useSelector, useDispatch } from 'react-redux';
import { ReactComponent as Logo } from '../../assets/logo.svg';
import {
    IconButton,
    Button,
    ButtonAnchor,
    AnchorComponent,
    LinkComponent,
} from '../../components/index';
import { clearSearch, setMapMode } from '../../actions/search';
import './Navbar.css';
import { useAppSelector } from 'store';
import selectIam from 'selectors/selectIam';
import { Environment } from 'services/Environment';
const { MobileMenu } = require('./MobileMenu');

export const Navbar = ({ navContainer = 'container' }) => {
    const { utm_details } = useSelector((state) => state.profile);
    const iam = useAppSelector(selectIam);
    const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
    const [mobileMenuShown, setMobileMenuShown] = useState(false);
    const [loginSheetShown, setLoginSheetShown] = useState(false);
    const [signupSheetShown, setSignupSheetShown] = useState(false);
    const dispatch = useDispatch();

    const renderAuthDependantNavLinks = () => {
        if (iam.authenticated) {
            if (isMobile) {
                return (
                    <ButtonAnchor
                        margin="0 10px 0 0"
                        children="Profile"
                        href="/profile"
                        target="_self"
                    />
                );
            }

            return (
                <>
                    <ButtonAnchor
                        margin="0 10px 0 0"
                        children="Profile"
                        href="/profile"
                        target="_self"
                    />
                    <Button
                        margin="0 10px 0 0"
                        children="Log out"
                        onClick={() => (window.location.href = '/logout')}
                    />
                </>
            );
        }

        if (!isMobile) {
            return (
                <Button
                    margin="0 10px 0 0"
                    children="Login Or Sign Up"
                    onClick={() => {
                        window.location.href = `${Environment.MARKETPLACE_URL}/login?redirect=${encodeURIComponent(window.location.href)}`;
                    }}
                />
            );
        }

        return null;
    };

    const PrimaryCtaButton = useCallback(() => {
        if (iam.authenticated && iam.educators && iam.educators.length > 0) {
            return (
                <ButtonAnchor
                    appearance="primary"
                    href={`https://admin.${Environment.DOMAIN_NAME}`}
                    children="Admin Dashboard"
                    desc="Switch to the Admin Dashboard"
                />
            );
        }

        return (
            <ButtonAnchor
                appearance="primary"
                href={`https://${Environment.DOMAIN_NAME}/for-educators`}
                children="For Educators"
                desc="Educators nav CTA"
            />
        );
    }, [iam]);

    return (
        <div className="navbar">
            <div className={'nav-contents ' + navContainer}>
                <LinkComponent
                    to="/"
                    onClick={() => {
                        dispatch(clearSearch());
                        dispatch(setMapMode(false));
                    }}
                >
                    <Logo
                        alt="cademy logo - blue graduation cap with Cademy written alongside"
                        className="navbar-logo"
                    />
                </LinkComponent>
                <div className="nav-elements-right">
                    {renderAuthDependantNavLinks()}
                    {isMobile ? (
                        <IconButton
                            onClick={() => setMobileMenuShown(true)}
                            children={<MenuIcon />}
                        />
                    ) : (
                        <PrimaryCtaButton />
                    )}
                    <MobileMenu
                        authenticated={iam.authenticated}
                        dispatch={dispatch}
                        mobileMenuShown={mobileMenuShown}
                        setMobileMenuShown={setMobileMenuShown}
                        uTMDetails={utm_details}
                    />
                    {/* Login sheet */}
                    <SideSheet
                        position={Position.BOTTOM}
                        isShown={loginSheetShown}
                        onCloseComplete={() => setLoginSheetShown(false)}
                    >
                        <Pane padding={30} height="90vh" className="nav-auth-sheet-container">
                            <AnchorComponent
                                href={Environment.ADMIN_APP_URL}
                                target="_self"
                                onClick={() => setLoginSheetShown(false)}
                            >
                                <Pane elevation={1} className="nav-auth-card">
                                    <Pane className="nav-auth-card-header">
                                        <ArrowRightIcon size={16} marginRight={10} marginTop={7} />
                                        <h2>Educator login</h2>
                                    </Pane>
                                    <p>For course providers to list and manage classes.</p>
                                </Pane>
                            </AnchorComponent>
                            <LinkComponent
                                to="/old-login"
                                onClick={() => setLoginSheetShown(false)}
                            >
                                <Pane elevation={1} className="nav-auth-card">
                                    <Pane className="nav-auth-card-header">
                                        <ArrowRightIcon size={16} marginRight={10} marginTop={7} />
                                        <h2>Learner login</h2>
                                    </Pane>
                                    <p>For students looking to manage their orders.</p>
                                </Pane>
                            </LinkComponent>
                        </Pane>
                    </SideSheet>

                    {/* Signup sheet */}
                    <SideSheet
                        position={Position.BOTTOM}
                        isShown={signupSheetShown}
                        onCloseComplete={() => setSignupSheetShown(false)}
                    >
                        <Pane padding={30} height="90vh" className="nav-auth-sheet-container">
                            <AnchorComponent
                                href={`${Environment.MARKETPLACE_URL}/create-educator`}
                                target="_self"
                                onClick={() => setSignupSheetShown(false)}
                            >
                                <Pane elevation={1} className="nav-auth-card">
                                    <Pane className="nav-auth-card-header">
                                        <ArrowRightIcon size={16} marginRight={10} marginTop={7} />
                                        <h2>Sign up as an Educator</h2>
                                    </Pane>
                                    <p>To list courses on Cademy and manage bookings.</p>
                                </Pane>
                            </AnchorComponent>
                            <AnchorComponent href={`${Environment.MARKETPLACE_URL}/login`}>
                                <Pane elevation={1} className="nav-auth-card">
                                    <Pane className="nav-auth-card-header">
                                        <ArrowRightIcon size={16} marginRight={10} marginTop={7} />
                                        <h2>Sign up as a Learner</h2>
                                    </Pane>
                                    <p>To book more quickly and manage orders.</p>
                                </Pane>
                            </AnchorComponent>
                        </Pane>
                    </SideSheet>
                </div>
            </div>
        </div>
    );
};
